import { type ColumnDef } from '@tanstack/react-table';
import type { GaugingRow } from '@/api/dataVault';
import { GlowBadge } from '../components/ui/GlowBadge';

interface ColumnFactoryOptions {
  onSelectWell?: (wellId: string) => void;
}

export function createGaugingColumns(opts: ColumnFactoryOptions): ColumnDef<GaugingRow, any>[] {
  const { onSelectWell } = opts;

  return [
    // 1. SITE
    {
      accessorKey: 'site_name',
      id: 'site_name',
      header: 'SITE',
      enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-mono-100 max-w-[200px] truncate block">
          {getValue<string>()}
        </span>
      ),
    },

    // 2. WELL
    {
      accessorKey: 'well_name',
      id: 'well_name',
      header: 'WELL',
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const name = getValue<string | null>();
        const wellId = row.original.well_id;

        if (!name) return <span className="text-mono-500">--</span>;

        if (onSelectWell && wellId) {
          return (
            <button
              className="text-cyan-400 hover:text-cyan-300 cursor-pointer"
              onClick={() => onSelectWell(wellId)}
            >
              {name}
            </button>
          );
        }

        return <span>{name}</span>;
      },
    },

    // 3. DATE
    {
      accessorKey: 'gauge_date',
      id: 'gauge_date',
      header: 'DATE',
      enableSorting: true,
      cell: ({ getValue }) => (
        <span className="text-mono-200">{getValue<string>()}</span>
      ),
    },

    // 4. DTW (ft)
    {
      accessorKey: 'depth_to_water_ft',
      id: 'depth_to_water_ft',
      header: 'DTW (ft)',
      enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue<number | null>();
        return (
          <span className="text-mono-100">
            {v != null ? v.toFixed(2) : '--'}
          </span>
        );
      },
    },

    // 5. ELEVATION (ft)
    {
      accessorKey: 'water_elevation_ft',
      id: 'water_elevation_ft',
      header: 'ELEVATION (ft)',
      enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue<number | null>();
        return (
          <span className="text-mono-100">
            {v != null ? v.toFixed(2) : '--'}
          </span>
        );
      },
    },

    // 6. TOC (ft)
    {
      accessorKey: 'casing_elevation_ft',
      id: 'casing_elevation_ft',
      header: 'TOC (ft)',
      enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue<number | null>();
        return (
          <span className="text-mono-200">
            {v != null ? v.toFixed(2) : '--'}
          </span>
        );
      },
    },

    // 7. DTP (ft)
    {
      accessorKey: 'depth_to_product_ft',
      id: 'depth_to_product_ft',
      header: 'DTP (ft)',
      enableSorting: true,
      cell: ({ getValue }) => {
        const v = getValue<number | null>();
        return (
          <span className="text-mono-100">
            {v != null ? v.toFixed(2) : '--'}
          </span>
        );
      },
    },

    // 9. LNAPL (ft)
    {
      accessorKey: 'product_thickness_ft',
      id: 'product_thickness_ft',
      header: 'LNAPL (ft)',
      enableSorting: false,
      cell: ({ getValue }) => {
        const v = getValue<number | null>();
        if (v != null && v > 0) {
          return (
            <span className="text-amber-400 font-medium">
              {v.toFixed(3)}
            </span>
          );
        }
        return <span className="text-mono-500">--</span>;
      },
    },

    // 9. PSH CORR — buoyancy-corrected GW elevation. The SG used in the
    // computation is shown in a hover-tooltip with provenance so PMs can
    // tell at a glance whether the value reflects a per-well override.
    {
      accessorKey: 'corrected_psh_ft',
      id: 'corrected_psh_ft',
      header: 'PSH CORR',
      enableSorting: false,
      cell: ({ getValue, row }) => {
        const v = getValue<number | null>();
        if (v == null) {
          return <span className="text-mono-500">--</span>;
        }
        const sg = row.original.specific_gravity;
        const src = row.original.specific_gravity_source;
        // A solid dot for per-well overrides, hollow for site/default.
        const dotClass =
          src === 'well'
            ? 'bg-cyan-400'
            : src === 'site'
              ? 'bg-transparent border border-mono-500'
              : 'bg-transparent border border-mono-700';
        const srcLabel =
          src === 'well' ? 'well-level' : src === 'site' ? 'site default' : 'fallback (0.800)';
        const tooltip =
          sg != null
            ? `Buoyancy-corrected with SG ${sg.toFixed(3)} (${srcLabel}).`
            : 'Buoyancy correction not applied (no product thickness).';
        return (
          <span className="inline-flex items-center gap-1.5" title={tooltip}>
            <span className="text-cyan-400">{v.toFixed(2)}</span>
            {sg != null && (
              <span
                aria-hidden
                className={`inline-block h-1.5 w-1.5 rounded-full ${dotClass}`}
              />
            )}
          </span>
        );
      },
    },

    // 10. STATUS — physical-state badges only (DRY, LNAPL).
    // Field-tech codes (DR, NS, etc.) live in the dedicated FLAG column.
    {
      id: 'status',
      header: 'STATUS',
      enableSorting: false,
      cell: ({ row }) => {
        const { is_dry, product_thickness_ft } = row.original;
        const badges: React.ReactNode[] = [];

        if (is_dry) {
          badges.push(
            <GlowBadge key="dry" variant="warning">DRY</GlowBadge>
          );
        }

        if (product_thickness_ft != null && product_thickness_ft > 0) {
          badges.push(
            <GlowBadge key="lnapl" variant="danger" className="ml-1">LNAPL</GlowBadge>
          );
        }

        return badges.length > 0 ? <>{badges}</> : <span className="text-mono-500">--</span>;
      },
    },

    // 11. FLAG CODE — dedicated sortable/filterable column
    {
      accessorKey: 'flag_code',
      id: 'flag_code',
      header: 'FLAG',
      enableSorting: true,
      cell: ({ getValue }) => {
        const code = getValue<string | null>();
        if (!code) return <span className="text-mono-500">--</span>;
        return <span className="text-amber-300 font-mono text-xs">{code}</span>;
      },
    },
  ];
}
